import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { showErrorMessages } from 'helpers/error';
import { reqGetOrder } from 'api/requests/main';
import { JobOrderDisplayType, JobOrderRawType } from 'components/JobGenerator/types';
import { parseJobOrderRaw, transformToDisplayData } from 'components/JobGenerator/jobOrderUtils';
import BasicModal from 'components/Common/BasicModal';
import InfoBox from 'components/JobGenerator/InfoBox';
import LoadingDots from 'components/Common/Loaders/LoadingDots';

type ROneModalProps = {
    open: boolean;
    jobOrderId: string | undefined;
    onClose: () => void;
};

const ROneModal = ({ open, jobOrderId, onClose }: ROneModalProps) => {
    const { t } = useTranslation();
    const [jobOrderDisplay, setJobOrderDisplay] = useState<JobOrderDisplayType>();
    const [error, setError] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const showData = !isLoading && !error && jobOrderDisplay;
    const showError = !isLoading && !jobOrderDisplay && error;

    useEffect(() => {
        let active = true;
        async function getOrder() {
            if (!jobOrderId) {
                return;
            }
            setIsLoading(true);
            setError(undefined);
            setJobOrderDisplay(undefined);

            try {
                const res = await reqGetOrder(jobOrderId);
                if (!active) {
                    return;
                }
                const data = res.data;

                if (data?.error?.message) {
                    setError(data.error.message);
                } else {
                    const jobOrder: JobOrderRawType = parseJobOrderRaw(data, jobOrderId);
                    setJobOrderDisplay(transformToDisplayData(jobOrder));
                }
            } catch (err) {
                if (active) {
                    showErrorMessages(err);
                }
            } finally {
                if (active) {
                    setIsLoading(false);
                }
            }
        }
        getOrder();

        return () => {
            active = false;
        };
    }, [jobOrderId]);

    return (
        <BasicModal open={open} onClose={onClose}>
            <Box className="r-one-modal">
                <Box className="r-one-modal-header">
                    <strong>R-One Job Order ID: </strong>
                    {jobOrderId}
                </Box>

                <Box className="r-one-modal-description">
                    {showData && <InfoBox jobOrderDisplayData={jobOrderDisplay} isNarrow={false} />}
                    {showError && <p>{error}</p>}
                    {isLoading && <LoadingDots />}
                </Box>
                <Box className="r-one-modal-btns">
                    <button onClick={onClose}>{t('web_app.close_btn')}</button>
                </Box>
            </Box>
        </BasicModal>
    );
};

export default ROneModal;
